import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook otimizado para buscar dados completos de Gestor e Cessionário
 * com fallback inteligente para CPF/RG quando o registro vinculado está incompleto.
 * 
 * Hierarquia de fallback:
 * 1. Dados do registro vinculado ao item (gestorId/cessionarioId)
 * 2. Se CPF/RG estiverem faltando, busca em outros contatos do mesmo cliente
 */
export function useItemGestorCessionario(
  gestorId?: string | null, 
  cessionarioId?: string | null,
  clienteId?: string | null  // Para fallback quando gestor/cessionário vinculado não tem CPF/RG
) {
  // Buscar dados completos do gestor com fallback para CPF/RG
  const { data: gestor, isLoading: loadingGestor } = useQuery({
    queryKey: ['gestor-completo', gestorId, clienteId],
    queryFn: async () => {
      if (!gestorId) return null;
      
      // 1. Buscar gestor vinculado ao item
      const { data: gestorItem, error } = await supabase
        .from('cliente_contato')
        .select('*')
        .eq('id', gestorId)
        .maybeSingle();
      
      if (error) {
        console.error('❌ Erro ao buscar gestor:', error);
        return null;
      }
      
      if (!gestorItem) return null;
      
      // 2. Se já tem CPF E RG, retornar direto (sem fallback necessário)
      if (gestorItem.cpf && gestorItem.rg) {
        return gestorItem;
      }
      
      // 3. FALLBACK: Buscar outro contato do cliente com CPF/RG
      const clienteIdFinal = gestorItem.cliente_id || clienteId;
      if (!clienteIdFinal) {
        return gestorItem;
      }
      
      // Buscar candidatos que tenham pelo menos CPF ou RG
      const { data: candidatos } = await supabase
        .from('cliente_contato')
        .select('cpf, rg')
        .eq('cliente_id', clienteIdFinal)
        .neq('id', gestorId) // Excluir o próprio registro
        .or('cpf.not.is.null,rg.not.is.null')
        .order('ordem', { ascending: true })
        .order('is_principal', { ascending: false })
        .limit(5);
      
      // 4. Encontrar o melhor candidato (priorizar quem tem ambos, depois quem tem o que falta)
      if (candidatos && candidatos.length > 0) {
        // Primeiro: candidato com CPF E RG
        const candidatoCompleto = candidatos.find(c => c.cpf && c.rg);
        // Segundo: qualquer candidato que tenha o campo faltante
        const candidatoParcial = candidatos.find(c => 
          (!gestorItem.cpf && c.cpf) || (!gestorItem.rg && c.rg)
        );
        
        const melhorCandidato = candidatoCompleto || candidatoParcial;
        
        if (melhorCandidato) {
          console.log('✅ Fallback de CPF/RG aplicado para gestor:', {
            gestorId,
            cpfOriginal: gestorItem.cpf,
            rgOriginal: gestorItem.rg,
            cpfFallback: melhorCandidato.cpf,
            rgFallback: melhorCandidato.rg
          });
          
          return {
            ...gestorItem,
            cpf: gestorItem.cpf || melhorCandidato.cpf,
            rg: gestorItem.rg || melhorCandidato.rg,
            _fallbackUsado: true  // Flag para debug/UI
          };
        }
      }
      
      return gestorItem;
    },
    enabled: !!gestorId,
    staleTime: 0, // Sem cache para sempre buscar dados atualizados
  });

  // Buscar dados completos do cessionário com fallback para CPF/RG
  const { data: cessionario, isLoading: loadingCessionario } = useQuery({
    queryKey: ['cessionario-completo', cessionarioId, clienteId],
    queryFn: async () => {
      if (!cessionarioId) return null;
      
      // 1. Buscar cessionário vinculado ao item
      const { data: cessionarioItem, error } = await supabase
        .from('cliente_cessionario')
        .select('*')
        .eq('id', cessionarioId)
        .maybeSingle();
      
      if (error) {
        console.error('❌ Erro ao buscar cessionário:', error);
        return null;
      }
      
      if (!cessionarioItem) return null;
      
      // 2. Se já tem CPF E RG, retornar direto
      if (cessionarioItem.cpf && cessionarioItem.rg) {
        return cessionarioItem;
      }
      
      // 3. FALLBACK: Buscar outro cessionário do cliente com CPF/RG
      const clienteIdFinal = cessionarioItem.cliente_id || clienteId;
      if (!clienteIdFinal) {
        return cessionarioItem;
      }
      
      const { data: candidatos } = await supabase
        .from('cliente_cessionario')
        .select('cpf, rg')
        .eq('cliente_id', clienteIdFinal)
        .neq('id', cessionarioId)
        .or('cpf.not.is.null,rg.not.is.null')
        .order('ordem', { ascending: true })
        .limit(5);
      
      // 4. Encontrar o melhor candidato
      if (candidatos && candidatos.length > 0) {
        const candidatoCompleto = candidatos.find(c => c.cpf && c.rg);
        const candidatoParcial = candidatos.find(c => 
          (!cessionarioItem.cpf && c.cpf) || (!cessionarioItem.rg && c.rg)
        );
        
        const melhorCandidato = candidatoCompleto || candidatoParcial;
        
        if (melhorCandidato) {
          console.log('✅ Fallback de CPF/RG aplicado para cessionário:', {
            cessionarioId,
            cpfOriginal: cessionarioItem.cpf,
            rgOriginal: cessionarioItem.rg,
            cpfFallback: melhorCandidato.cpf,
            rgFallback: melhorCandidato.rg
          });
          
          return {
            ...cessionarioItem,
            cpf: cessionarioItem.cpf || melhorCandidato.cpf,
            rg: cessionarioItem.rg || melhorCandidato.rg,
            _fallbackUsado: true
          };
        }
      }
      
      return cessionarioItem;
    },
    enabled: !!cessionarioId,
    staleTime: 0,
  });

  return {
    gestor: gestor || null,
    cessionario: cessionario || null,
    isLoading: loadingGestor || loadingCessionario,
  };
}
